import React, { useState, useCallback, useEffect } from 'react';
import { M365User, MailboxState } from '../../types';
import { useGraphClient } from '../../hooks/useGraphClient';
import * as exchangeService from '../../services/exchangeService';
import Modal from '../Shared/Modal';
import Spinner from '../Shared/Spinner';
import UserSearch from './UserSearch';

interface MailboxSettingsProps {
  user: M365User;
  /** Neutralise la section pendant qu'un offboarding est en cours */
  disabled?: boolean;
}

/** Action destructrice en attente de confirmation */
interface PendingConfirm {
  title: string;
  body: string;
  label: string;
  run: () => Promise<void>;
}

/**
 * Réglages courants d'une boîte mail : message d'absence, transfert, délégations.
 *
 * Séparée de la checklist d'offboarding à dessein. Retirer un transfert six mois
 * après un départ est de l'entretien courant : le faire depuis une liste de dix
 * actions destructrices, en prenant soin de toutes les décocher, est le genre de
 * manipulation où l'on se trompe une fois de trop.
 *
 * Aucune suppression n'est déduite d'un champ vide — vide, c'est aussi l'état
 * avant d'avoir tapé quoi que ce soit. Chaque retrait est un bouton explicite,
 * confirmé.
 */
const MailboxSettings: React.FC<MailboxSettingsProps> = ({ user, disabled = false }) => {
  const { getFunctionToken } = useGraphClient();
  const mailbox = user.mail || user.userPrincipalName;

  const [settings, setSettings] = useState<MailboxState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<'autoReply' | 'forward' | 'delegate' | null>(null);
  const [autoReplyText, setAutoReplyText] = useState('');
  const [recipient, setRecipient] = useState<{ email: string; name: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getFunctionToken();
      const response = await exchangeService.getMailboxSettings(mailbox, token);
      setSettings(response.details as MailboxState);
    } catch (err: any) {
      setError(err?.message || "Impossible de lire l'état de la boîte.");
      setSettings(null);
    } finally {
      setIsLoading(false);
    }
  }, [getFunctionToken, mailbox]);

  useEffect(() => {
    load();
  }, [load]);

  /** Exécute une écriture puis relit l'état, pour n'afficher que du réel */
  const runAction = useCallback(
    async (action: (token: string) => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getFunctionToken();
        await action(token);
        await load();
      } catch (err: any) {
        setError(err?.message || 'Erreur inconnue');
      } finally {
        setBusy(false);
        setConfirm(null);
        setEditing(null);
      }
    },
    [getFunctionToken, load]
  );

  const openAutoReplyEditor = () => {
    setAutoReplyText(settings?.autoReplyMessage || '');
    setFormError(null);
    setEditing('autoReply');
  };

  const openRecipientPicker = (mode: 'forward' | 'delegate') => {
    setRecipient(null);
    setFormError(null);
    setEditing(mode);
  };

  const autoReplyLabel = (state?: string) => {
    switch (state) {
      case 'Disabled':
        return 'désactivé';
      case 'Enabled':
        return 'actif en permanence';
      case 'Scheduled':
        return 'programmé';
      default:
        return state || '—';
    }
  };

  const autoReplyActive =
    settings?.autoReplyState === 'Enabled' || settings?.autoReplyState === 'Scheduled';

  const actionsDisabled = disabled || busy || isLoading;

  return (
    <div className="bg-white border border-ms-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-6 py-3 bg-ms-gray-50 rounded-t-lg">
        <h4 className="text-sm font-semibold text-ms-gray-700">Réglages de la boîte mail</h4>
        <button
          onClick={load}
          disabled={actionsDisabled}
          className="text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors disabled:opacity-50"
        >
          Actualiser
        </button>
      </div>

      {isLoading && !settings && (
        <div className="flex items-center gap-3 px-6 py-6">
          <Spinner size="sm" />
          <span className="text-sm text-ms-gray-500">
            Lecture de la boîte dans Exchange… (jusqu'à une minute au premier appel)
          </span>
        </div>
      )}

      {error && (
        <div className="mx-6 my-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-ms-red">{error}</p>
        </div>
      )}

      {settings && (
        <div className="divide-y divide-ms-gray-100">
          {/* Message d'absence */}
          <div className="flex items-start gap-4 px-6 py-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ms-gray-800">Message d'absence</p>
              <p className="text-xs text-ms-gray-500 mt-0.5">
                {autoReplyLabel(settings.autoReplyState)}
              </p>
              {autoReplyActive && settings.autoReplyMessage && (
                <p className="text-xs text-ms-gray-400 mt-1 line-clamp-2">
                  « {settings.autoReplyMessage} »
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={openAutoReplyEditor}
                disabled={actionsDisabled}
                className="text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors disabled:opacity-50"
              >
                {autoReplyActive ? 'Modifier' : 'Activer'}
              </button>
              {autoReplyActive && (
                <button
                  onClick={() =>
                    setConfirm({
                      title: "Désactiver le message d'absence",
                      body: `Les expéditeurs qui écrivent à ${mailbox} ne recevront plus de réponse automatique.`,
                      label: 'Désactiver',
                      run: () =>
                        runAction((token) => exchangeService.disableAutoReply(mailbox, token)),
                    })
                  }
                  disabled={actionsDisabled}
                  className="text-xs text-ms-red hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  Désactiver
                </button>
              )}
            </div>
          </div>

          {/* Transfert */}
          <div className="flex items-start gap-4 px-6 py-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ms-gray-800">Transfert des mails</p>
              <p className="text-xs text-ms-gray-500 mt-0.5 break-all">
                {settings.forwardingAddress
                  ? `vers ${settings.forwardingAddress}${
                      settings.deliverAndForward ? ' — copie conservée' : ' — sans copie'
                    }`
                  : 'aucun'}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => openRecipientPicker('forward')}
                disabled={actionsDisabled}
                className="text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors disabled:opacity-50"
              >
                {settings.forwardingAddress ? 'Modifier' : 'Configurer'}
              </button>
              {settings.forwardingAddress && (
                <button
                  onClick={() =>
                    setConfirm({
                      title: 'Supprimer le transfert',
                      body: `Les mails reçus par ${mailbox} ne seront plus transférés vers ${settings.forwardingAddress}.`,
                      label: 'Supprimer',
                      run: () =>
                        runAction((token) => exchangeService.removeForwarding(mailbox, token)),
                    })
                  }
                  disabled={actionsDisabled}
                  className="text-xs text-ms-red hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>

          {/* Délégations */}
          <div className="px-6 py-4">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ms-gray-800">
                  Délégations FullAccess ({settings.delegates?.length ?? 0})
                </p>
                {!settings.delegates?.length && (
                  <p className="text-xs text-ms-gray-500 mt-0.5">aucune</p>
                )}
              </div>
              <button
                onClick={() => openRecipientPicker('delegate')}
                disabled={actionsDisabled}
                className="flex-shrink-0 text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors disabled:opacity-50"
              >
                Ajouter
              </button>
            </div>

            {!!settings.delegates?.length && (
              <ul className="mt-3 space-y-1.5">
                {settings.delegates.map((delegate) => (
                  <li
                    key={delegate}
                    className="flex items-center justify-between gap-3 px-3 py-2 bg-ms-gray-50 rounded-md"
                  >
                    <span className="text-xs text-ms-gray-700 truncate">{delegate}</span>
                    <button
                      onClick={() =>
                        setConfirm({
                          title: 'Révoquer la délégation',
                          body: `${delegate} n'aura plus accès à la boîte ${mailbox}.`,
                          label: 'Révoquer',
                          run: () =>
                            runAction((token) =>
                              exchangeService.removeDelegate(mailbox, delegate, token)
                            ),
                        })
                      }
                      disabled={actionsDisabled}
                      className="flex-shrink-0 text-xs text-ms-red hover:text-red-700 px-2 py-1 rounded hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      Révoquer
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Édition du message d'absence */}
      <Modal
        isOpen={editing === 'autoReply'}
        onClose={() => setEditing(null)}
        title="Message d'absence"
        confirmLabel="Enregistrer"
        isLoading={busy}
        onConfirm={() => {
          if (!autoReplyText.trim()) {
            setFormError("Saisissez le message, ou fermez et utilisez « Désactiver ».");
            return;
          }
          runAction((token) => exchangeService.setAutoReply(mailbox, autoReplyText.trim(), token));
        }}
      >
        <textarea
          value={autoReplyText}
          onChange={(e) => {
            setAutoReplyText(e.target.value);
            setFormError(null);
          }}
          rows={5}
          placeholder="Bonjour, cette personne ne fait plus partie de l'entreprise. Pour toute demande, veuillez contacter…"
          className="w-full px-3 py-2 border border-ms-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ms-blue/30 focus:border-ms-blue resize-none"
        />
        <p className="text-xs text-ms-gray-400 mt-1">
          Envoyé à tous les expéditeurs, internes et externes. Pour arrêter la réponse
          automatique, utilisez le bouton « Désactiver » plutôt que d'effacer le texte.
        </p>
        {formError && <p className="text-sm text-ms-red mt-3">{formError}</p>}
      </Modal>

      {/* Choix d'un destinataire : transfert ou délégation */}
      <Modal
        isOpen={editing === 'forward' || editing === 'delegate'}
        onClose={() => setEditing(null)}
        title={editing === 'forward' ? 'Transférer les mails vers' : 'Ajouter un délégué'}
        confirmLabel="Enregistrer"
        isLoading={busy}
        onConfirm={() => {
          if (!recipient) {
            setFormError('Choisissez un destinataire.');
            return;
          }
          runAction((token) =>
            editing === 'forward'
              ? exchangeService.setMailForwarding(mailbox, recipient.email, token)
              : exchangeService.delegateMailbox(mailbox, recipient.email, token)
          );
        }}
      >
        {recipient ? (
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-ms-gray-50 border border-ms-gray-200 rounded-md">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ms-gray-800 truncate">
                {recipient.name || recipient.email}
              </p>
              {recipient.name && (
                <p className="text-xs text-ms-gray-500 truncate">{recipient.email}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setRecipient(null)}
              className="flex-shrink-0 text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors"
            >
              Changer
            </button>
          </div>
        ) : (
          <UserSearch
            inline
            label=""
            placeholder="Nom, email ou UPN..."
            excludeUserId={user.id}
            onSelectUser={(u) => {
              setFormError(null);
              setRecipient({ email: u.mail || u.userPrincipalName, name: u.displayName });
            }}
            // Exchange refuse une délégation vers une adresse externe, mais accepte
            // un transfert : la sortie de secours n'a de sens que pour le transfert.
            onUseRawAddress={
              editing === 'forward'
                ? (address) => {
                    setFormError(null);
                    setRecipient({ email: address, name: '' });
                  }
                : undefined
            }
          />
        )}
        {formError && <p className="text-sm text-ms-red mt-3">{formError}</p>}
      </Modal>

      {/* Confirmation des retraits */}
      <Modal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title || ''}
        confirmLabel={confirm?.label || 'Confirmer'}
        confirmVariant="danger"
        isLoading={busy}
        onConfirm={() => confirm?.run()}
      >
        <p className="text-sm text-ms-gray-700">{confirm?.body}</p>
      </Modal>
    </div>
  );
};

export default MailboxSettings;
